import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence, Reorder, useDragControls } from 'framer-motion';
import { gsap } from 'gsap';
import { getResults, exportReport, exportClientSummary, downloadReport, reorderFindings, updateCompletenessItem } from '../api/client';
import FlagCard from '../components/FlagCard';

// Drag handle + Reorder.Item wrapper for one finding card. The handle (not the whole
// card) owns the drag gesture (dragListener={false} + dragControls.start on pointerdown)
// so dragging never fights with clicks/typing inside FlagCard's own buttons and inputs.
function ReorderableFinding({ finding, onToggleInclude, onUpdate }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={finding.id}
      dragListener={false}
      dragControls={controls}
      style={{ listStyle: 'none' }}
    >
      <FlagCard
        finding={finding}
        onToggleInclude={onToggleInclude}
        onUpdate={onUpdate}
        dragHandle={
          <div
            onPointerDown={(e) => controls.start(e)}
            title="Drag to reorder"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 32, flexShrink: 0, cursor: 'grab', color: 'var(--text-label)',
              touchAction: 'none', userSelect: 'none',
            }}
          >
            {/* Plain-text grip glyphs render inconsistently across fonts (can look like
                nothing at all) — an inline SVG always shows the same grip-dot icon. */}
            <svg width="14" height="22" viewBox="0 0 14 22" fill="currentColor" aria-hidden="true">
              <circle cx="4"  cy="4"  r="1.7" />
              <circle cx="10" cy="4"  r="1.7" />
              <circle cx="4"  cy="11" r="1.7" />
              <circle cx="10" cy="11" r="1.7" />
              <circle cx="4"  cy="18" r="1.7" />
              <circle cx="10" cy="18" r="1.7" />
            </svg>
          </div>
        }
      />
    </Reorder.Item>
  );
}

const prefersReduced = typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const SOURCE_OPTIONS = [
  { value: '', label: 'All sources' },
  { value: 'agent1', label: 'AI Analysis' },
  { value: 'dre', label: 'Rules Engine' },
  { value: 'synthesis', label: 'Cross-reference' },
  { value: 'completeness', label: 'Completeness' },
];

// Per-report details the agent fills in on the Export tab. These feed only the
// report's presentation (the "Before we get into it" note, the Opportunity page,
// and the financial tables) — never the analysis. Blank fields are simply omitted.
const EMPTY_REPORT_INPUTS = {
  client_name: '',
  agent_note: '',
  opportunity_points: [
    { tag: '', headline: '', text: '' },
    { tag: '', headline: '', text: '' },
    { tag: '', headline: '', text: '' },
  ],
  investment: {
    asking_price: '', transfer_tax: '', notary: '', mortgage_advice: '',
    valuation: '', inspection: '', agency_fee: '',
    mortgage_payment: '', vve_fee: '', leasehold: '', insurance: '', municipal_tax: '',
    price_per_m2: '',
  },
};

// Closing-cost + monthly-cost field definitions (label + input key), rendered as
// two simple € input tables on the Export tab.
const CLOSING_FIELDS = [
  ['asking_price', 'Asking price'],
  ['transfer_tax', 'Transfer tax (blank = auto 2%)'],
  ['notary', 'Notary fees'],
  ['mortgage_advice', 'Mortgage advice & arrangement'],
  ['valuation', 'Valuation report'],
  ['inspection', 'Structural / technical inspection'],
  ['agency_fee', "Hendriks Housing buyer's agency fee"],
];
const MONTHLY_FIELDS = [
  ['mortgage_payment', 'Mortgage payment (indicative)'],
  ['vve_fee', 'VvE monthly fee'],
  ['leasehold', 'Leasehold canon (erfpacht) — optional'],
  ['insurance', 'Building & contents insurance'],
  ['municipal_tax', 'Municipal & water board taxes'],
];

export default function ResultsDashboard() {
  const { id: routeId } = useParams();
  // Share builds (REACT_APP_SHARE_ID set) always show this one review, regardless
  // of the URL — the backend enforces the same restriction independently.
  const id = process.env.REACT_APP_SHARE_ID || routeId;
  // Agent-view builds (REACT_APP_AGENT_VIEW=1) are a permanent, read-only link for an
  // HH agent to browse one analysis's findings with no laptop/backend dependency —
  // report generation and the "back to upload" edit screen aren't part of that use case.
  const AGENT_VIEW = process.env.REACT_APP_AGENT_VIEW === '1';
  const [data, setData]           = useState(null);
  const [activeTab, setActiveTab] = useState('overzicht');
  const [flagFilter, setFlagFilter]     = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [exporting, setExporting]       = useState(false);
  const [exportFilename, setExportFilename] = useState('');
  const [included, setIncluded]   = useState({});   // finding id -> bool (red/amber only)
  const [orderIds, setOrderIds]   = useState([]);   // drag order, ids only, single-colour view
  const [lang, setLang] = useState('en');           // shared output language
  const [useSelection, setUseSelection] = useState(false);
  const [reportInputs, setReportInputs] = useState(EMPTY_REPORT_INPUTS);
  const root = useRef(null);
  const didReveal = useRef(false);

  const LANGS = [
    { v: 'en', label: 'English' }, { v: 'nl', label: 'Nederlands' },
    { v: 'de', label: 'Deutsch' }, { v: 'fr', label: 'Français' }, { v: 'es', label: 'Español' },
  ];

  // Landing here straight off the progress page's redirect can occasionally race the
  // backend finishing its last DB writes for the run — a fetch failure right at that
  // moment used to leave the page either stuck or crashing. Retry quietly instead of
  // surfacing that as an error; the results are already there or seconds away.
  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    function load() {
      getResults(id).then(res => {
        if (cancelled) return;
        if (!res || !res.review) { retry(); return; }
        setData(res);
      }).catch(() => { if (!cancelled) retry(); });
    }
    function retry() {
      attempts += 1;
      if (attempts > 8) return;       // ~10s of retries, then give up quietly
      setTimeout(() => { if (!cancelled) load(); }, 750);
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  /* Report inputs persist locally per review, so the agent's note + figures survive a
     page refresh (no backend/DB change — kept purely in the browser). */
  useEffect(() => {
    try {
      const saved = localStorage.getItem(`dp_report_inputs_${id}`);
      if (saved) setReportInputs({ ...EMPTY_REPORT_INPUTS, ...JSON.parse(saved) });
      else setReportInputs(EMPTY_REPORT_INPUTS);
    } catch { /* ignore malformed cache */ }
  }, [id]);
  useEffect(() => {
    try { localStorage.setItem(`dp_report_inputs_${id}`, JSON.stringify(reportInputs)); } catch { /* quota */ }
  }, [id, reportInputs]);

  const setRI  = (patch) => setReportInputs(p => ({ ...p, ...patch }));

  // Transfer tax tracks 2% of the asking price automatically as the agent types,
  // as long as they haven't typed their own figure into that field — the moment
  // they override it, it stops following the asking price.
  const parseMoney = (s) => {
    const n = parseFloat(String(s).replace(/[€,\s]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };
  const autoTransferTax = (askingStr) => {
    const asking = parseMoney(askingStr);
    return asking ? String(Math.round(asking * 0.02)) : '';
  };
  const setInv = (k, v) => setReportInputs(p => {
    const prevInv = p.investment;
    const nextInv = { ...prevInv, [k]: v };
    if (k === 'asking_price') {
      const transferIsAuto = prevInv.transfer_tax === '' || prevInv.transfer_tax === autoTransferTax(prevInv.asking_price);
      if (transferIsAuto) nextInv.transfer_tax = autoTransferTax(v);
    }
    return { ...p, investment: nextInv };
  });
  const setOpp = (i, k, v) => setReportInputs(p => ({
    ...p, opportunity_points: p.opportunity_points.map((o, idx) => (idx === i ? { ...o, [k]: v } : o)),
  }));

  /* Seed the selection map from saved checked_for_report once data arrives */
  useEffect(() => {
    if (!data) return;
    const m = {};
    data.findings.forEach(f => {
      if (f.flag_type === 'red' || f.flag_type === 'amber') m[f.id] = !!f.checked_for_report;
    });
    setIncluded(m);
  }, [data]);

  const onToggleInclude = (fid, _flag, val) =>
    setIncluded(prev => ({ ...prev, [fid]: val }));

  // Drag-reordering only makes sense within one colour section at a time (that's the
  // unit the generated report groups findings by) and with no source filter narrowing
  // the visible set — otherwise a drag would silently leave same-colour findings that
  // are filtered out of view in an undefined relative order.
  const canReorder = ['red', 'amber', 'green'].includes(flagFilter) && !sourceFilter;

  useEffect(() => {
    if (!data || !canReorder) { setOrderIds([]); return; }
    const nextIds = data.findings.filter(f => f.flag_type === flagFilter && f.reviewer_status !== 'dismissed').map(f => f.id);
    setOrderIds(prev => {
      // A finding update (confirm/dismiss/checkbox/flag colour) replaces `data` wholesale,
      // which would otherwise re-derive this list from the findings array's default order
      // and silently discard whatever the reviewer just dragged. Only re-derive when the
      // set of ids actually changed (filter switch, or a finding entered/left this colour) —
      // otherwise keep the existing drag order as-is.
      const prevSet = new Set(prev);
      const sameMembers = prev.length === nextIds.length && nextIds.every(fid => prevSet.has(fid));
      return sameMembers ? prev : nextIds;
    });
  }, [data, flagFilter, sourceFilter, canReorder]);

  const handleReorder = (newOrder) => {
    setOrderIds(newOrder);
    reorderFindings(id, newOrder).catch(() => {});
  };

  // Re-file a finding live after a reviewer colour override so it lands in the
  // correct tab/count immediately (the backend returns the effective flag_type).
  const onFindingUpdate = (updated) =>
    setData(prev => prev
      ? { ...prev, findings: prev.findings.map(f => (f.id === updated.id ? { ...f, ...updated } : f)) }
      : prev);

  // Reviewer correction of a completeness row (e.g. checker got present/missing wrong).
  // Refetches rather than patching locally — the backend merges the override at read
  // time, and re-deriving "reset back to what?" correctly on the client isn't worth it.
  const [editingDocType, setEditingDocType] = useState(null);
  const changeCompleteness = async (docType, status) => {
    setEditingDocType(null);
    try {
      await updateCompletenessItem(id, docType, status);
      const fresh = await getResults(id);
      setData(fresh);
    } catch {
      alert('Could not update — check the backend is running.');
    }
  };

  /* Header reveal + stat counters once data is in */
  useLayoutEffect(() => {
    if (!data) return;
    const ctx = gsap.context(() => {
      if (!prefersReduced && !didReveal.current) {
        didReveal.current = true;   // reveal once, not on every live update
        gsap.fromTo('.line-mask > span',
          { yPercent: 112 },
          { yPercent: 0, duration: 0.85, ease: 'power3.out', stagger: 0.1, delay: 0.05 },
        );
      }
      gsap.utils.toArray('.res-stat').forEach((el, i) => {
        const target = parseInt(el.dataset.target || '0', 10);
        if (prefersReduced) { el.textContent = target; return; }
        const obj = { v: 0 };
        gsap.to(obj, {
          v: target, duration: 1.2, ease: 'power2.out', delay: 0.25 + i * 0.08,
          snap: { v: 1 },
          onUpdate: () => { el.textContent = Math.floor(obj.v); },
        });
      });
    }, root);
    return () => ctx.revert();
  }, [data]);

  if (!data) return (
    <div className="page" style={{ textAlign: 'center', paddingTop: 80 }}>
      <p className="text-muted">Loading results...</p>
    </div>
  );

  const { review, findings, completeness, audit_trail, stats, run } = data;

  const filteredFindings = findings.filter(f => {
    if (f.reviewer_status === 'dismissed') return false;
    if (flagFilter && f.flag_type !== flagFilter) return false;
    if (sourceFilter && f.source !== sourceFilter) return false;
    return true;
  });

  // Live counts off the current findings, so the top numbers update the instant
  // a reviewer changes a finding's colour (no page refresh needed).
  const liveRed   = findings.filter(f => f.flag_type === 'red' && f.reviewer_status !== 'dismissed').length;
  const liveAmber = findings.filter(f => f.flag_type === 'amber' && f.reviewer_status !== 'dismissed').length;
  // Green is never curated (report always shows a fixed first 12, see html_report.py),
  // but dismissed/suppressed greens are reviewer-rejected or unverified — drop them so
  // the headline matches the Findings tab and the report's front-page count.
  const liveGreen = findings.filter(f => f.flag_type === 'green' && f.reviewer_status !== 'dismissed' && !f.suppressed).length;

  async function handleExport(mode = 'standard') {
    setExporting(true);
    setExportFilename('');
    try {
      const result = await exportReport(id, mode, lang, reportInputs);
      setExportFilename(result.filename);
    } catch {
      alert('Report generation failed — check the backend is running and try again.');
    } finally {
      setExporting(false);
    }
  }

  async function handleClientSummary() {
    setExporting(true);
    setExportFilename('');
    try {
      const result = await exportClientSummary(id, lang, useSelection ? 'custom' : 'standard');
      setExportFilename(result.filename);
    } catch {
      alert('Client summary generation failed — check the backend is running and try again.');
    } finally {
      setExporting(false);
    }
  }

  async function handleDownload() {
    try {
      const blob = await downloadReport(id, exportFilename);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      alert('Download failed — try again.');
    }
  }

  const topRed = findings.filter(f => f.flag_type === 'red').slice(0, 3);

  // A missing/insufficient-document red reflects an incomplete dossier, not a
  // dangerous property — keep paperwork gaps from reading as "high risk". Mirrors
  // the report's _is_completeness logic so the platform and PDF agree. Missing-document
  // findings now carry source='completeness' (single source of truth), so we check that
  // directly instead of fragile title-keyword matching; the two DRE "insufficient count"
  // rules are recognised by their stable rule ids.
  const isCompleteness = (f) =>
    f.source === 'completeness'
    || ['vve_notulen_insufficient', 'vve_jaarrekening_insufficient'].includes(f.dre_rule);
  const genuineRed = findings.filter(f => f.flag_type === 'red' && !isCompleteness(f)).length;

  const verdict = genuineRed > 0
    ? { label: 'Points to address', cls: 'badge-red', statement: 'review the items below with the seller.' }
    : (liveRed > 0 || liveAmber > 0)
      ? { label: 'A few points to review', cls: 'badge-amber', statement: 'a few items to clarify or complete.' }
      : { label: 'Looks healthy', cls: 'badge-green', statement: 'no material issues found.' };

  return (
    <div className="page theme-cream" ref={root}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 40 }}>
        {!AGENT_VIEW && (
          <div style={{ marginBottom: 18 }}>
            <Link to={`/reviews/${id}`} style={{ fontSize: 13, color: 'var(--text-label)' }}>← Back to upload</Link>
          </div>
        )}
        <div className="eyebrow" style={{ marginBottom: 16 }}>
          Analysis Report — Case #{String(id).padStart(2, '0')}
        </div>
        <div className="page-header-row" style={{ alignItems: 'flex-end' }}>
          <div style={{ minWidth: 0 }}>
            <h1 className="display-lg" style={{ marginBottom: 14 }}>
              <span className="line-mask"><span>{review.property_address}</span></span>
              <span className="line-mask"><span style={{
                fontSize: '0.62em', color: 'var(--text-secondary)',
              }}><em className="display-accent">{liveRed} red, {liveAmber} amber — {verdict.statement}</em></span></span>
            </h1>
            <p className="text-muted" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span className={`badge ${verdict.cls}`}>{verdict.label}</span>
              <span>
                Analysis complete · {new Date(run?.completed_at || review.created_at).toLocaleDateString('en-GB')}
              </span>
            </p>
          </div>
          {!AGENT_VIEW && (
            <button className="btn btn-primary btn-dot" onClick={() => setActiveTab('exporteren')}>
              Download Report
            </button>
          )}
        </div>
      </div>

      {/* Stats — big numerals */}
      <div className="numerals-row" style={{ marginBottom: 44 }}>
        {[
          { cls: 'red',   value: liveRed,               label: 'Red flags' },
          { cls: 'amber', value: liveAmber,             label: 'Amber flags' },
          { cls: 'green', value: liveGreen,             label: 'Clear' },
          { cls: stats.missing_docs > 0 ? 'red' : '', value: stats.missing_docs, label: 'Missing docs' },
          { cls: '',      value: stats.total_documents, label: 'Documents' },
        ].map((s, i) => (
          <div className={`numeral ${s.cls}`} key={i}>
            <div className="value res-stat" data-target={s.value}>0</div>
            <div className="label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="tabs--pills">
        {[
          { key: 'overzicht',   label: 'Overview' },
          { key: 'bevindingen', label: `Findings (${findings.length})` },
          { key: 'volledigheid',label: `Completeness (${stats.missing_docs} missing)` },
          { key: 'auditspoor',  label: 'Audit trail' },
          ...(AGENT_VIEW ? [] : [{ key: 'exporteren', label: 'Export' }]),
        ].map(t => (
          <button
            key={t.key}
            className={`tab-pill ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >

          {/* Overview */}
          {activeTab === 'overzicht' && (
            <div>
              {stats.missing_docs > 0 && (
                <div style={{
                  background: 'var(--card)',
                  borderRadius: 16, borderLeft: '4px solid var(--red)',
                  boxShadow: 'var(--shadow)',
                  padding: '18px 24px', marginBottom: 20,
                  display: 'flex', alignItems: 'center', gap: 16,
                }}>
                  <div style={{ color: 'var(--red)', fontSize: 20, flexShrink: 0 }}>⚠</div>
                  <div>
                    <strong style={{ color: 'var(--text)', fontSize: '0.95rem' }}>
                      {stats.missing_docs} document{stats.missing_docs !== 1 ? 's' : ''} missing from this package
                    </strong>
                    <p style={{ fontSize: 13, color: 'var(--text-label)', marginTop: 3 }}>
                      See the Completeness tab for the full list.
                    </p>
                  </div>
                </div>
              )}

              {topRed.length > 0 && (
                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 16, color: 'var(--text)' }}>
                    Top red findings
                  </h3>
                  {topRed.map(f => <FlagCard key={f.id} finding={f} onToggleInclude={onToggleInclude} onUpdate={onFindingUpdate} />)}
                  {findings.filter(f => f.flag_type === 'red').length > 3 && (
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ marginTop: 4 }}
                      onClick={() => { setActiveTab('bevindingen'); setFlagFilter('red'); }}
                    >
                      View all red findings →
                    </button>
                  )}
                </div>
              )}

              {topRed.length === 0 && (
                <div className="card" style={{ textAlign: 'center', padding: '48px 32px' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
                  <h3 style={{ color: 'var(--green)', fontWeight: 600, marginBottom: 6 }}>No red findings</h3>
                  <p className="text-muted text-sm">No high-risk issues were detected in this package.</p>
                </div>
              )}
            </div>
          )}

          {/* Findings */}
          {activeTab === 'bevindingen' && (
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
                {['', 'red', 'amber', 'green'].map(v => (
                  <button
                    key={v}
                    className={`btn btn-sm ${flagFilter === v ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setFlagFilter(v)}
                  >
                    {v === ''      ? 'All' :
                     v === 'red'   ? '● Red' :
                     v === 'amber' ? '● Amber' : '● Clear'}
                    {' '}({findings.filter(f => f.reviewer_status !== 'dismissed' && (v === '' || f.flag_type === v)).length})
                  </button>
                ))}
                <select
                  className="form-control"
                  style={{ width: 'auto', fontSize: 13, borderRadius: 999, padding: '7px 14px' }}
                  value={sourceFilter}
                  onChange={e => setSourceFilter(e.target.value)}
                >
                  {SOURCE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
                {canReorder
                  ? 'Drag ⠿ to reorder — the generated report follows this order.'
                  : 'Select a single colour above (and clear the source filter) to drag-reorder findings for the report.'}
              </p>

              {filteredFindings.length === 0 && (
                <div className="empty-state">
                  <p>No findings match this filter.</p>
                </div>
              )}

              {canReorder ? (
                <Reorder.Group as="div" axis="y" values={orderIds} onReorder={handleReorder}>
                  {orderIds.map(fid => {
                    const f = findings.find(x => x.id === fid);
                    if (!f) return null;
                    return (
                      <ReorderableFinding key={f.id} finding={f}
                        onToggleInclude={onToggleInclude} onUpdate={onFindingUpdate} />
                    );
                  })}
                </Reorder.Group>
              ) : (
                filteredFindings.map(f => <FlagCard key={f.id} finding={f} onToggleInclude={onToggleInclude} onUpdate={onFindingUpdate} />)
              )}
            </div>
          )}

          {/* Completeness */}
          {activeTab === 'volledigheid' && (
            <div className="card">
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 20 }}>Completeness check</h3>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Importance</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {completeness.map((item, i) => {
                      const tierLabel = { M: 'Mandatory', I: 'Important', O: 'Optional' }[item.tier] || 'Mandatory';
                      let badge;
                      if (item.status === 'red') {
                        badge = <span className="badge badge-red">✗ Missing</span>;
                      } else if (item.status === 'amber') {
                        badge = <span className="badge badge-amber">⚠ Recommended</span>;
                      } else if (item.found) {
                        badge = <span className="badge badge-green">✓ Present</span>;
                      } else {
                        badge = <span className="badge" style={{ background: '#F3F4F6', color: '#6B7280' }}>○ Not provided</span>;
                      }
                      const editing = editingDocType === item.doc_type;
                      // Which of the 4 reviewer-facing states this row currently reflects,
                      // so the matching button shows as active whether or not it's overridden.
                      const currentState = item.overridden
                        ? item.override_state
                        : item.status === 'red' ? 'missing'
                        : item.status === 'amber' ? 'recommended'
                        : item.found ? 'present' : 'not_provided';
                      return (
                        <tr key={i}>
                          <td style={{ fontWeight: 500, color: 'var(--text)' }}>{item.doc_label || item.doc_type}</td>
                          <td style={{ color: 'var(--text-label)', fontSize: 13 }}>{tierLabel}</td>
                          <td>
                            {badge}
                            {item.overridden && <span style={{ fontSize: 11, color: 'var(--text-label)', marginLeft: 6 }}>· edited</span>}
                          </td>
                          <td>
                            {editing ? (
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <div style={{ display: 'inline-flex', border: '1px solid var(--border)',
                                              borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                                  {[
                                    { key: 'present',      label: 'Present',      color: '#2F8E54' },
                                    { key: 'missing',      label: 'Missing',      color: '#C8402F' },
                                    { key: 'recommended',  label: 'Recommended',  color: '#D89713' },
                                    { key: 'not_provided', label: 'Not provided', color: '#6B7280' },
                                  ].map((s, ci) => (
                                    <button
                                      key={s.key}
                                      type="button"
                                      onClick={() => changeCompleteness(item.doc_type, s.key)}
                                      style={{
                                        border: 'none',
                                        borderLeft: ci ? '1px solid var(--border)' : 'none',
                                        cursor: 'pointer',
                                        padding: '4px 10px',
                                        fontSize: 11,
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                        background: currentState === s.key ? s.color : 'transparent',
                                        color: currentState === s.key ? '#fff' : 'var(--text-label)',
                                      }}
                                    >
                                      {s.label}
                                    </button>
                                  ))}
                                </div>
                                {item.overridden && (
                                  <button type="button" onClick={() => changeCompleteness(item.doc_type, null)}
                                          style={{ border: 'none', background: 'none', cursor: 'pointer',
                                                   fontSize: 11, color: 'var(--text-label)', textDecoration: 'underline' }}>
                                    reset
                                  </button>
                                )}
                                <button type="button" onClick={() => setEditingDocType(null)}
                                        style={{ border: 'none', background: 'none', cursor: 'pointer',
                                                 fontSize: 11, color: 'var(--text-label)' }}>
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <button type="button" onClick={() => setEditingDocType(item.doc_type)}
                                      title="Correct this row"
                                      style={{ border: '1px solid var(--border)', background: 'none', cursor: 'pointer',
                                               borderRadius: 6, padding: '3px 8px', fontSize: 11, color: 'var(--text-label)' }}>
                                Edit
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Audit trail */}
          {activeTab === 'auditspoor' && (
            <div className="card">
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 20 }}>Audit trail</h3>
              {audit_trail.length === 0 && <p className="text-muted">No audit data available.</p>}
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Event</th>
                      <th>Document</th>
                      <th>Summary</th>
                      <th>Tokens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit_trail.map((entry, i) => (
                      <tr key={i}>
                        <td className="text-sm text-muted">
                          {entry.created_at ? new Date(entry.created_at).toLocaleTimeString('en-GB') : ''}
                        </td>
                        <td>
                          <span className="badge badge-gray" style={{ fontSize: 11 }}>
                            {entry.event_type}
                          </span>
                        </td>
                        <td className="text-sm">{entry.doc_type || '—'}</td>
                        <td className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {(entry.result_summary || '').substring(0, 120)}
                        </td>
                        <td className="text-sm text-muted">{entry.tokens_used || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Export */}
          {activeTab === 'exporteren' && (() => {
            const selRed   = (data.findings || []).filter(f => f.flag_type === 'red'   && included[f.id]).length;
            const selAmber = (data.findings || []).filter(f => f.flag_type === 'amber' && included[f.id]).length;
            return (
            <div style={{ display: 'grid', gap: 20, maxWidth: 560 }}>

              {/* Output language (applies to all exports below) */}
              <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 14 }}>Output language</strong>
                <select className="form-control" value={lang} onChange={e => setLang(e.target.value)}
                        style={{ width: 'auto', fontSize: 13, borderRadius: 8, padding: '7px 12px' }}>
                  {LANGS.map(l => <option key={l.v} value={l.v}>{l.label}</option>)}
                </select>
                <span className="text-muted text-sm">findings are translated automatically</span>
              </div>

              {/* Report details — agent note, opportunity, financial figures */}
              <div className="card">
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 6 }}>Report details</h3>
                <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
                  These appear in the generated report only. They don’t affect the analysis or findings,
                  and are saved in your browser so they survive a refresh. Leave anything blank to hide it.
                </p>

                {/* Client name + agent note */}
                <label className="text-sm" style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>Client name (optional)</label>
                <input className="form-control" type="text" value={reportInputs.client_name}
                       onChange={e => setRI({ client_name: e.target.value })}
                       placeholder="e.g. Sarah" style={{ marginBottom: 16 }} />

                <label className="text-sm" style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
                  Your note — the “Before we get into it” section
                </label>
                <textarea className="form-control" rows={6} value={reportInputs.agent_note}
                          onChange={e => setRI({ agent_note: e.target.value })}
                          placeholder="Write your personal introduction to the client here. Leave a blank line between paragraphs. If left empty, a professional default note is used."
                          style={{ marginBottom: 6, resize: 'vertical', lineHeight: 1.5 }} />
                <p className="text-muted text-sm" style={{ marginBottom: 20 }}>
                  Appears on page 2, next to Jeroen Hendriks’ photo.
                </p>

                {/* Opportunity points */}
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>“Why this is a strong opportunity” (optional)</div>
                <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
                  Up to 3 short selling points. Add a headline to include one; leave all blank to skip this page.
                </p>
                {reportInputs.opportunity_points.map((o, i) => (
                  <div key={i} style={{ display: 'grid', gap: 6, marginBottom: 12,
                                        gridTemplateColumns: '120px 1fr', alignItems: 'start' }}>
                    <input className="form-control" type="text" value={o.tag}
                           onChange={e => setOpp(i, 'tag', e.target.value)} placeholder="Tag (e.g. Value)" />
                    <input className="form-control" type="text" value={o.headline}
                           onChange={e => setOpp(i, 'headline', e.target.value)} placeholder="Headline" />
                    <div />
                    <input className="form-control" type="text" value={o.text}
                           onChange={e => setOpp(i, 'text', e.target.value)} placeholder="One or two supporting sentences" />
                  </div>
                ))}

                {/* Financial figures */}
                <div style={{ fontWeight: 600, fontSize: 13, margin: '18px 0 4px' }}>One-time costs to close (€)</div>
                <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
                  Not in the seller documents — enter your own figures. Fill any and the total is calculated for you.
                </p>
                {CLOSING_FIELDS.map(([k, label]) => (
                  <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span className="text-sm" style={{ flex: 1, color: 'var(--text-label)' }}>{label}</span>
                    <input className="form-control" type="text" inputMode="numeric"
                           value={reportInputs.investment[k]} onChange={e => setInv(k, e.target.value)}
                           placeholder="€" style={{ width: 130, textAlign: 'right' }} />
                  </div>
                ))}

                <div style={{ fontWeight: 600, fontSize: 13, margin: '18px 0 12px' }}>Monthly cost to own (€ / month)</div>
                {MONTHLY_FIELDS.map(([k, label]) => (
                  <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span className="text-sm" style={{ flex: 1, color: 'var(--text-label)' }}>{label}</span>
                    <input className="form-control" type="text" inputMode="numeric"
                           value={reportInputs.investment[k]} onChange={e => setInv(k, e.target.value)}
                           placeholder="€ / mo" style={{ width: 130, textAlign: 'right' }} />
                  </div>
                ))}

                <div style={{ fontWeight: 600, fontSize: 13, margin: '18px 0 12px' }}>Property reference</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span className="text-sm" style={{ flex: 1, color: 'var(--text-label)' }}>Price per m² (optional)</span>
                  <input className="form-control" type="text" inputMode="numeric"
                         value={reportInputs.investment.price_per_m2} onChange={e => setInv('price_per_m2', e.target.value)}
                         placeholder="€ / m²" style={{ width: 130, textAlign: 'right' }} />
                </div>
              </div>

              {/* Custom (curated) report */}
              <div className="card">
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 6 }}>Custom report</h3>
                <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
                  Confirm a finding, then tick “In report” (Findings tab) to include it — then generate a
                  tailored report — the executive summary adapts to your selection.
                </p>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18,
                  padding: '12px 16px', background: 'rgba(19,19,19,0.03)', borderRadius: 12,
                }}>
                  <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--red)' }}>{selRed}</span>
                  <span className="text-sm text-muted">red ·</span>
                  <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--amber)' }}>{selAmber}</span>
                  <span className="text-sm text-muted">amber selected</span>
                </div>
                <button className="btn btn-primary" onClick={() => handleExport('custom')} disabled={exporting}>
                  {exporting ? 'Generating…' : 'Generate custom report'}
                </button>
              </div>

              {/* Download result */}
              {exportFilename && (
                <div className="card" style={{ borderLeft: '4px solid var(--green)' }}>
                  <div style={{ fontSize: 14, color: 'var(--green)', fontWeight: 500, marginBottom: 12 }}>
                    ✓ Ready: {exportFilename}
                  </div>
                  <button className="btn btn-primary" onClick={handleDownload}>↓ Download PDF</button>
                </div>
              )}
            </div>
            );
          })()}

        </motion.div>
      </AnimatePresence>
    </div>
  );
}
